import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { ConfirmationService, Message, MessageService, TreeNode } from 'primeng/api';
import { FAZILService } from 'src/app/core/services/fazil.service';
import { User, UserService } from 'src/app/core';
import { AdministracionService } from '../administracion.service';
import { UIService } from 'src/app/core/services/ui.service';

import { UsuarioModel } from 'src/app/modelo/usuario.model';
import { PerfilModel } from 'src/app/modelo/Perfil.model';
import { EliminarData } from 'src/app/modelo/EliminarData';
import { TareaModel } from 'src/app/modelo/Tarea.model';
import { Md5 } from 'md5-typescript';

@Component({
    selector: 'app-admin-usuarios',
    templateUrl: './admin-usuarios.component.html',
    styleUrls: ['./admin-usuarios.component.scss']
})
export class AdminUsuariosComponent implements OnInit {

    //Variables de configuración/mensajes
    appConfig = environment.settings;
    errors: Object = {};
    msgs: Message[] = [];

    //Variables con objeto de edición
    currentUser: User;
    usuarios: UsuarioModel[] = [];
    usuariosSeleccionados: UsuarioModel[];
    usuariosFiltrados: UsuarioModel[];
    usuario: UsuarioModel = new UsuarioModel();
    perfilesNoAsignados: PerfilModel[] = [];
    perfilesAsignados: PerfilModel[] = [];

    //Variables auxiliares
    mostrarEditor: boolean = false;
    mostrarEditorPrivilegios: boolean = false;
    cols: any[];
    multiSortMeta = [];
    usuarioCodigo: string = "";
    cargandoDataInit: boolean = false;
    procesando: boolean = false;
    ui: UIService;

    constructor(
        private router: Router,
        private userService: UserService,
        private fazilService: FAZILService,
        private uiService: UIService,
        private administracionService: AdministracionService,
        private confirmationService: ConfirmationService,
        private breadcrumbService: BreadcrumbService,
        private messageService: MessageService,
    ) {
        this.ui = uiService;
        this.cargarDataConstructor();
    }

    cargarDataConstructor() {
        this.cargandoDataInit = true;
        this.breadcrumbService.setItems([
            { label: 'Administración de Usuarios', routerLink: ['administracion/usuarios'] }
        ]);
        this.cols = [
            { field: 'codigo', header: 'Cédula de Identidad', visible: true },
            { field: 'nombre', header: 'Nombre', visible: true },
            { field: 'username', header: 'Nombre de Usuario', visible: true },
            { field: 'email', header: 'Correo Electrónico', visible: true },
        ];
        this.multiSortMeta.push({ field: 'nombre', order: 1 });
    }

    ngOnInit(): void {
        this.userService.currentUser.subscribe(userData => {
            this.currentUser = userData;
            if (this.currentUser && this.currentUser.uuid) {
                this.cargarDataInit();
            }
        });
    }

    async cargarDataInit() {
        this.usuarios = await this.administracionService.getUsuariosNoTemporalesData();
        this.cargandoDataInit = false;
    }

    resetVariables(): void {
        this.ui.limpiarMessages();
        this.mostrarEditor = false;
        this.mostrarEditorPrivilegios = false;
        this.perfilesAsignados = []; //encerar la lista de asignaciones
        this.usuarioCodigo = "";
        this.usuario = new UsuarioModel();
    }

    nuevoUsuario(): void {
        this.resetVariables();
    }

    editarUsuario(usuarioModel: UsuarioModel): void {
        this.resetVariables();
        this.administracionService.getUsuarioModel(usuarioModel.uuid).subscribe(async data => {
            this.usuario = data['user'];
            if (this.usuario.contrasenia && this.usuario.contrasenia != '') {
                this.usuario.password = Md5.init(this.usuario.contrasenia); //Encryptar la contraseña
            }
            this.usuarioCodigo = this.usuario.code;
            this.mostrarEditor = true;
        });
    }

    formPrincipalCompleto() {
        return true;
    }

    guardarUsuario(): void {
        this.ui.limpiarMessages();
        if (!this.formPrincipalCompleto()) {
            this.ui.mostrarMensaje("error", `${this.appConfig.ux.formIvalid}`);
        } else {
            //Establecer valores UX
            //Actualizar contraseña si es el caso, es decir si se modificó
            if (this.usuario.contrasenia && this.usuario.contrasenia != '') {
                this.usuario.password = Md5.init(this.usuario.contrasenia); //Encryptar la contraseña
            }
            this.usuario.code = this.usuarioCodigo;
            this.administracionService.guardarUsuario(this.usuario).subscribe(
                async (data) => {
                    this.usuario = data;
                    this.usuarios = await this.administracionService.getUsuariosNoTemporalesData();
                    this.ui.mostrarMensaje("success", `Actualización del usuario ${this.usuario.name} se realizó con éxito.`);
                    this.mostrarEditor = false;
                },
                (err) => {
                    this.ui.mostrarError(err);
                }
            );
        }
    }

    confirmarBorrarUsuario(usuario: UsuarioModel): void {
        this.resetVariables();
        this.confirmationService.confirm({
            message: "¿Está seguro de eliminar el usuario <strong>" + usuario.name + "</strong>?. No será posible recuperar la información.",
            accept: () => {
                this.borrarUsuario(usuario);
            },
        });
    }

    borrarUsuario(usuario: UsuarioModel): void {
        this.administracionService.borrarUsuario(usuario.uuid).subscribe(
            async (data) => {
                this.usuarios = await this.administracionService.getUsuariosNoTemporalesData();
                this.ui.mostrarMensaje("success", `El usuario ${usuario.name} ha sido eliminado con éxito.`);
            },
            (err) => {
                this.ui.mostrarError(err);
            }
        );
    }

    /**
    * Editar usuario privilegios.
    */
    async editarUsuarioPrivilegios(usuario: UsuarioModel): Promise<void> {
        this.resetVariables();
        this.usuario = usuario;
        //Carga lista de perfiles asignados al usuario
        this.perfilesAsignados = await this.administracionService.getUsuarioPerfilesData(this.usuario.id);
        //Carga lista de todos perfiles disponibles
        let perfilesAuxiliar = await this.administracionService.getPerfilesData();
        this.perfilesNoAsignados = this.difference(perfilesAuxiliar ? perfilesAuxiliar : [], this.perfilesAsignados ? this.perfilesAsignados : []);
        this.mostrarEditorPrivilegios = true;
    }

    confirmarGuardarUsuarioPrivilegios(): void {
        this.ui.limpiarMessages();
        if (this.perfilesAsignados && this.perfilesAsignados.length > 0) {
            this.confirmationService.confirm({
                message: "¿Está seguro de actualizar los <strong> perfiles </strong> del usuario <strong>" + this.usuario.name + "</strong>?.",
                accept: () => {
                    this.guardarUsuarioPrivilegios();
                },
            });
        } else {
            this.ui.mostrarMensaje("warn", `No es posible dejar sin privilegios al usuario ${this.usuario.name}, asigne el rol de usuario como mínimo.`);
        }
    }

    guardarUsuarioPrivilegios(): void {
        const data = {
            "uuid": this.usuario.uuid,
            "perfiles": this.perfilesAsignados ? this.perfilesAsignados : [],
        };
        this.administracionService.guardarUsuarioPrivilegios(data).subscribe(
            (data) => {
                this.ui.mostrarMensaje("success", `Actualización de los perfiles del usuario ${this.usuario.name} se realizó con éxito.`);
                this.mostrarEditorPrivilegios = false;
            },
            (err) => {
                this.ui.mostrarError(err);
            }
        );
    }

    /**
    ** Métodos de formulario
    **/
    verificarUsuarioExistente(event) {
        const cedula = this.usuarioCodigo.split('-')[0];
        this.usuario = this.usuarios.find(val => val.code == cedula.trim()) ? this.usuarios.find(val => val.code == cedula.trim()) : new UsuarioModel();
    }

    ///////////////////////////////////////////////////////////////////////////
    //Control de acceso a acciones en pantalla
    // TODO: implementar estos métodos en todas las pantallas
    ///////////////////////////////////////////////////////////////////////////
    puedeEjecutar(accion: string): boolean {
        if (!this.currentUser || !this.currentUser.tareas) { //No tiene esta tarea
            return false;
        }

        const routerLink = this.router.url.replace("/fazil/", "");
        let tarea: TareaModel = new TareaModel;
        tarea = this.currentUser.tareas.find(val => routerLink.startsWith(val.routerLink));

        if (!tarea) { //No tiene esta tarea
            return false;
        }
        if (accion == "lectura") {
            return tarea.puedeLeer;
        } else if (accion == "crear") {
            return tarea.puedeCrear;
        } else if (accion == "escritura") {
            return tarea.puedeEscribir;
        } else if (accion == "borrado") {
            return tarea.puedeBorrar;
        } else if (accion == "aprobar") {
            return tarea.puedeAprobar;
        }
        return false;
    }

    /**
    * Lista de tareas para los roles del usuario autenticado
    */
    getTareasPermisos(routeLink: string): Promise<any> {
        return this.fazilService.getTareasPermisos(routeLink).toPromise();
    }

    difference(arr1: PerfilModel[], arr2: PerfilModel[]) {
        const result = [];
        var i = 0,
            j = 0;
        var flag = false;

        /* For array 1 */
        for (i = 0; i < arr1.length; i++) {

            /* Reseting the flag and the 
            other array iterator */
            j = 0;
            flag = false
            while (j != arr2.length) {
                if (arr1[i].id == arr2[j].id) {
                    flag = true;
                    break;
                }
                j++;
            }

            /* If value is not present in the 
            second array then push that value 
            to the resultant array */
            if (!flag) {
                result.push(arr1[i]);
            }
        }
        flag = false;

        /* For array 2 */
        for (i = 0; i < arr2.length; i++) {

            /* Reseting the flag and the 
            other array iterator */
            j = 0;
            flag = false
            while (j != arr1.length) {
                if (arr2[i].id == arr1[j].id) {
                    flag = true;
                    break;
                }
                j++;
            }

            /* If value is not present in the
            first array then push that value 
            to the resultant array */
            if (!flag) {
                result.push(arr2[i]);
            }
        }
        return result;
    }

}